<!-- Status -->
<div class="d-flex align-items-center gap-4 mb-1">
  <label class="form-label-v2 text-muted mb-0 text-nowrap">{{ 'profile.two_factor_auth' | translate }}</label>
  @if (isEnabled) {
    <hc-badge color="success" variant="light" size="small" class="ms-4">
      <lucide-icon name="shield"></lucide-icon>
      {{ 'profile.2fa_enabled' | translate }}
    </hc-badge>
  } @else {
    <hc-badge color="primary" variant="light" size="small" class="ms-4">
      <lucide-icon name="shield-off"></lucide-icon>
      {{ 'profile.2fa_disabled' | translate }}
    </hc-badge>
  }
</div>

<p class="text-muted small mb-2">{{ 'profile.authenticators.description' | translate }}</p>

@if (isImpersonating) {
  <div class="alert alert-info small">
    <lucide-icon name="info" class="me-1"></lucide-icon>
    {{ 'profile.authenticators.impersonation_note' | translate }}
  </div>
}

@if (showReloginHint) {
  <div class="alert alert-info small">
    <lucide-icon name="info" class="me-1"></lucide-icon>
    {{ 'profile.authenticators.relogin_hint' | translate }}
  </div>
}

@if (loadError) {
  <div class="alert alert-danger small">
    <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
    {{ loadError }}
  </div>
}

<!-- Actions -->
@if (canManage) {
  <div class="mt-2 d-flex flex-wrap gap-2">
    @if (isEnabled) {
      <hc-button variant="primary" leftIcon="plus" (clicked)="openAddModal()">
        {{ 'profile.authenticators.add' | translate }}
      </hc-button>
      <hc-button variant="secondary" leftIcon="key-round" (clicked)="openRecoveryCodesModal('show')">
        {{ 'profile.authenticators.show_recovery_codes' | translate }}
      </hc-button>
      <hc-button variant="secondary" leftIcon="refresh-cw" (clicked)="openRecoveryCodesModal('regenerate')">
        {{ 'profile.authenticators.generate_new_codes' | translate }}
      </hc-button>
      <hc-button variant="destructive-secondary" leftIcon="shield-off" (clicked)="openDisableModal()">
        {{ 'profile.disable_2fa' | translate }}
      </hc-button>
    } @else {
      <hc-button variant="primary" leftIcon="shield" (clicked)="openAddModal()">
        {{ 'profile.enable_2fa' | translate }}
      </hc-button>
    }
  </div>
}

<!-- Authenticator list -->
@if (isEnabled) {
  <div class="mt-3">
    <h6 class="mb-2 authenticators-title">
      <div class="authenticators-section-icon">
        <lucide-icon name="smartphone"></lucide-icon>
      </div>
      {{ 'profile.authenticators.title' | translate }}
    </h6>

    @if (isLoadingMethods) {
      <div class="text-center py-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
        </div>
      </div>
    }

    @if (!isLoadingMethods && methods.length === 0 && !loadError) {
      <app-empty-state
        icon="smartphone"
        size="sm"
        [title]="'profile.authenticators.empty_title'"
        [description]="'profile.authenticators.empty_description'">
      </app-empty-state>
    }

    @if (!isLoadingMethods && methods.length > 0) {
      <div class="authenticators-list">
        @for (method of methods; track method.id) {
          <div class="authenticator-card card mb-2">
            <div class="card-body">
              <div class="auth-row">
                <div class="auth-icon">
                  <lucide-icon name="smartphone" class="text-primary"></lucide-icon>
                </div>
                <div class="auth-meta">
                  <h6 class="auth-label">{{ method.label }}</h6>
                  <div class="auth-details text-muted small">
                    <span class="auth-detail-item">
                      <lucide-icon name="calendar-plus"></lucide-icon>
                      {{ 'profile.authenticators.added' | translate }}:
                      <strong>{{ method.confirmed_at | date: 'dd.MM.yyyy HH:mm' }}</strong>
                    </span>
                    <span class="auth-detail-item">
                      <lucide-icon name="clock"></lucide-icon>
                      {{ 'profile.authenticators.last_used' | translate }}:
                      <strong>
                        @if (method.last_used_at) {
                          {{ method.last_used_at | date: 'dd.MM.yyyy HH:mm' }}
                        } @else {
                          {{ 'profile.authenticators.never_used' | translate }}
                        }
                      </strong>
                    </span>
                  </div>
                </div>
                @if (canManage) {
                  <hc-button
                    variant="destructive-secondary"
                    leftIcon="circle-x"
                    (clicked)="openRemoveModal(method)"
                    [ariaLabel]="'profile.authenticators.remove' | translate">
                    {{ 'profile.authenticators.remove' | translate }}
                  </hc-button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }
  </div>
}

<!-- Add authenticator -->
@if (showAddModal) {
  <app-two-factor-add-modal
    [isFirstAuthenticator]="!isEnabled"
    (added)="onAuthenticatorAdded($event)"
    (sessionRequired)="onSessionRequired()"
    (closed)="closeAddModal()">
  </app-two-factor-add-modal>
}

<!-- Remove authenticator -->
@if (methodToRemove) {
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-backdrop fade show" tabindex="0" role="button" aria-label="Close modal" (click)="closeRemoveModal()" (keyup.enter)="closeRemoveModal()"></div>
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <lucide-icon name="circle-x" class="text-danger me-2"></lucide-icon>
            {{ 'profile.authenticators.remove_title' | translate }}
          </h5>
          <button type="button" class="btn-close" aria-label="Close" (click)="closeRemoveModal()" [disabled]="isRemoving"></button>
        </div>
        <div class="modal-body">
          <p>{{ 'profile.authenticators.remove_message' | translate: { label: methodToRemove.label } }}</p>

          @if (isLastAuthenticator) {
            <div class="alert alert-warning small">
              <lucide-icon name="triangle-alert" class="me-1"></lucide-icon>
              {{ 'profile.authenticators.remove_last_warning' | translate }}
            </div>
          }

          <div class="mb-3">
            <label class="form-label-v2" for="remove-authenticator-password">
              {{ 'profile.authenticators.password_label' | translate }}
              <span class="required-indicator">*</span>
            </label>
            <hc-input type="password"
              inputId="remove-authenticator-password"
              autocomplete="current-password"
              [(ngModel)]="removePassword"
              [placeholder]="'profile.enter_password' | translate"
              (keyup.enter)="confirmRemove()">
            </hc-input>
          </div>

          @if (removeError) {
            <div class="alert alert-danger small mb-0">
              <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
              {{ removeError }}
            </div>
          }
        </div>
        <div class="modal-footer">
          <hc-button variant="secondary" size="md" (clicked)="closeRemoveModal()" [disabled]="isRemoving">
            {{ 'cancel' | translate }}
          </hc-button>
          <hc-button
            variant="danger"
            size="md"
            [disabled]="!removePassword || isRemoving"
            [loading]="isRemoving"
            (clicked)="confirmRemove()">
            {{ 'profile.authenticators.remove' | translate }}
          </hc-button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Disable 2FA -->
@if (showDisableModal) {
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-backdrop fade show" tabindex="0" role="button" aria-label="Close modal" (click)="closeDisableModal()" (keyup.enter)="closeDisableModal()"></div>
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <lucide-icon name="shield-off" class="text-danger me-2"></lucide-icon>
            {{ 'profile.disable_2fa_title' | translate }}
          </h5>
          <button type="button" class="btn-close" aria-label="Close" (click)="closeDisableModal()" [disabled]="isDisabling"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning small">
            <lucide-icon name="triangle-alert" class="me-1"></lucide-icon>
            {{ 'profile.disable_2fa_warning' | translate }}
          </div>

          <div class="mb-3">
            <label class="form-label-v2" for="disable-2fa-password">
              {{ 'profile.authenticators.password_label' | translate }}
              <span class="required-indicator">*</span>
            </label>
            <hc-input type="password"
              inputId="disable-2fa-password"
              autocomplete="current-password"
              [(ngModel)]="disablePassword"
              [placeholder]="'profile.enter_password' | translate"
              (keyup.enter)="confirmDisable()">
            </hc-input>
          </div>

          @if (disableError) {
            <div class="alert alert-danger small mb-0">
              <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
              {{ disableError }}
            </div>
          }
        </div>
        <div class="modal-footer">
          <hc-button variant="secondary" size="md" (clicked)="closeDisableModal()" [disabled]="isDisabling">
            {{ 'cancel' | translate }}
          </hc-button>
          <hc-button
            variant="danger"
            size="md"
            [disabled]="!disablePassword || isDisabling"
            [loading]="isDisabling"
            (clicked)="confirmDisable()">
            {{ 'profile.disable_2fa' | translate }}
          </hc-button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Recovery codes: password first, then the codes -->
@if (showRecoveryCodesModal) {
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-backdrop fade show" tabindex="0" role="button" aria-label="Close modal" (click)="closeRecoveryCodesModal()" (keyup.enter)="closeRecoveryCodesModal()"></div>
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <lucide-icon [name]="recoveryCodesAction === 'regenerate' ? 'refresh-cw' : 'key-round'" class="text-primary me-2"></lucide-icon>
            {{ (recoveryCodesAction === 'regenerate' ? 'profile.regenerate_codes_title' : 'profile.authenticators.recovery_codes_title') | translate }}
          </h5>
          <button type="button" class="btn-close" aria-label="Close" (click)="closeRecoveryCodesModal()" [disabled]="isLoadingRecoveryCodes"></button>
        </div>
        <div class="modal-body">
          @if (recoveryCodes.length > 0) {
            <app-recovery-codes-list
              [codes]="recoveryCodes"
              [context]="recoveryCodesAction === 'regenerate' ? 'regenerated' : 'view'">
            </app-recovery-codes-list>
          } @else if (hasNoRecoveryCodesLeft) {
            <div class="alert alert-warning small mb-0">
              <lucide-icon name="triangle-alert" class="me-1"></lucide-icon>
              {{ 'profile.authenticators.no_recovery_codes_left' | translate }}
            </div>
          } @else {
            @if (recoveryCodesAction === 'regenerate') {
              <div class="alert alert-warning small">
                <lucide-icon name="triangle-alert" class="me-1"></lucide-icon>
                {{ 'profile.authenticators.regenerate_warning' | translate }}
              </div>
            }
            <p class="text-muted">{{ 'profile.authenticators.password_prompt' | translate }}</p>

            <div class="mb-3">
              <label class="form-label-v2" for="recovery-codes-password">
                {{ 'profile.authenticators.password_label' | translate }}
                <span class="required-indicator">*</span>
              </label>
              <hc-input type="password"
                inputId="recovery-codes-password"
                autocomplete="current-password"
                [(ngModel)]="recoveryPassword"
                [placeholder]="'profile.enter_password' | translate"
                (keyup.enter)="submitRecoveryPassword()">
              </hc-input>
            </div>

            @if (recoveryError) {
              <div class="alert alert-danger small mb-0">
                <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
                {{ recoveryError }}
              </div>
            }
          }
        </div>
        <div class="modal-footer">
          @if (recoveryCodes.length > 0) {
            <hc-button variant="primary" size="md" (clicked)="closeRecoveryCodesModal()">
              {{ 'done' | translate }}
            </hc-button>
          } @else if (hasNoRecoveryCodesLeft) {
            <hc-button variant="secondary" size="md" (clicked)="closeRecoveryCodesModal()">
              {{ 'cancel' | translate }}
            </hc-button>
            <hc-button variant="primary" size="md" leftIcon="refresh-cw" (clicked)="switchToRegenerate()">
              {{ 'profile.authenticators.generate_new_codes' | translate }}
            </hc-button>
          } @else {
            <hc-button variant="secondary" size="md" (clicked)="closeRecoveryCodesModal()" [disabled]="isLoadingRecoveryCodes">
              {{ 'cancel' | translate }}
            </hc-button>
            <hc-button
              variant="primary"
              size="md"
              [disabled]="!recoveryPassword || isLoadingRecoveryCodes"
              [loading]="isLoadingRecoveryCodes"
              (clicked)="submitRecoveryPassword()">
              {{ (recoveryCodesAction === 'regenerate' ? 'profile.authenticators.generate_new_codes' : 'profile.authenticators.show_recovery_codes') | translate }}
            </hc-button>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Session never passed 2FA -->
@if (showSessionRequiredModal) {
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-backdrop fade show" tabindex="0" role="button" aria-label="Close modal" (click)="closeSessionRequiredModal()" (keyup.enter)="closeSessionRequiredModal()"></div>
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <lucide-icon name="lock-keyhole" class="text-primary me-2"></lucide-icon>
            {{ 'profile.authenticators.session_required_title' | translate }}
          </h5>
          <button type="button" class="btn-close" aria-label="Close" (click)="closeSessionRequiredModal()" [disabled]="isLoggingOut"></button>
        </div>
        <div class="modal-body">
          <p class="mb-0">{{ 'profile.authenticators.session_required_message' | translate }}</p>
        </div>
        <div class="modal-footer">
          <hc-button variant="secondary" size="md" (clicked)="closeSessionRequiredModal()" [disabled]="isLoggingOut">
            {{ 'cancel' | translate }}
          </hc-button>
          <hc-button variant="primary" size="md" leftIcon="log-out" [loading]="isLoggingOut" [disabled]="isLoggingOut" (clicked)="logInAgain()">
            {{ 'profile.authenticators.log_in_again' | translate }}
          </hc-button>
        </div>
      </div>
    </div>
  </div>
}
